/**
 * Shared formatting utilities for the Per Diem app.
 */

import { Linking, Platform } from 'react-native';

/**
 * Format a number as USD currency.
 *
 * `compact: true` abbreviates only values large enough that the full form
 * would wrap or dominate the layout. By default that's $1M+ (→ $1.2M);
 * thousands render in full with commas, since `$81,600` reads better than
 * `$81.6K` in most surfaces. Callers with tight labels (chart axes) can
 * opt into a lower threshold via `compactThreshold`.
 */
export function formatCurrency(
  value: number,
  options: { compact?: boolean; decimals?: number; compactThreshold?: number } = {},
): string {
  const { compact = false, decimals = 2, compactThreshold = 1_000_000 } = options;

  if (compact) {
    const abs = Math.abs(value);
    if (abs >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(1)}B`;
    if (abs >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
    if (abs >= compactThreshold) return `$${(value / 1_000).toFixed(1)}K`;
    // Below the threshold — fall through to the full-precision formatter
    // with no fractional digits, since compact callers don't want cents.
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

/** Format a number as a percentage string. */
export function formatPercent(value: number, decimals = 1): string {
  return `${(value * 100).toFixed(decimals)}%`;
}

/** Format a nullable percentage with an explicit + sign for positive changes. */
export function formatSignedPercent(value: number | null, decimals = 1): string | null {
  if (value == null) return null;
  const prefix = value > 0 ? '+' : '';
  return `${prefix}${formatPercent(value, decimals)}`;
}

/** Format a Date or ISO string as a readable date. */
export { formatDate } from './date-format';

/** Format a date as "X ago" / "just now". */
export function formatRelativeTime(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const diffMs = Date.now() - d.getTime();
  const diffMins = Math.floor(diffMs / 60_000);
  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  return `${diffDays}d ago`;
}

/** Convert snake_case or camelCase to Title Case. */
export function toTitleCase(str: string): string {
  return str
    .replace(/_/g, ' ')
    .replace(/([A-Z])/g, ' $1')
    .replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase())
    .trim();
}

/**
 * Sign-aware currency formatting for deltas (day change, gain/loss, etc).
 * Defaults to whole dollars — callers that want cents can override `decimals`.
 */
export function formatCurrencyChange(
  value: number,
  options: { decimals?: number } = {},
): string {
  const prefix = value >= 0 ? '+' : '';
  return prefix + formatCurrency(value, { decimals: options.decimals ?? 0 });
}

/**
 * Price per share/unit. Sub-$1 prices (crypto, penny stocks) need more
 * precision than cents or they render as $0.00.
 */
export function formatSharePrice(value: number): string {
  const decimals = value >= 1 ? 2 : value >= 0.01 ? 4 : 8;
  return formatCurrency(value, { decimals });
}

/** Format basis points or raw number as compact currency for chart axes. */
export function formatChartYAxis(value: number): string {
  // Chart axes benefit from tighter labels, so keep the lower $1K threshold.
  return formatCurrency(value, { compact: true, decimals: 0, compactThreshold: 1_000 });
}

/** Clamp a number between min and max. */
export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/** Open an external URL in a browser-safe way across native and web. */
export function openExternalUrl(url: string): void {
  if (Platform.OS === 'web' && typeof globalThis.open === 'function') {
    const openedWindow = globalThis.open(url, '_blank');
    if (openedWindow) {
      openedWindow.opener = null;
      return;
    }

    if (typeof globalThis.location?.assign === 'function') {
      globalThis.location.assign(url);
    }
    return;
  }

  void Linking.openURL(url).catch(() => undefined);
}

/** Prefer local legal pages on web so localhost and preview builds work before deploy. */
export function getLegalUrl(kind: 'privacy' | 'terms' | 'security'): string {
  if (Platform.OS === 'web' && typeof globalThis.location !== 'undefined') {
    const paths = {
      privacy: '/privacy-policy',
      terms: '/terms-of-service',
      security: '/security-policy',
    } as const;
    const isLocalHost = ['localhost', '127.0.0.1', '::1'].includes(globalThis.location.hostname);
    const path = isLocalHost ? `${paths[kind]}.html` : paths[kind];
    return `${globalThis.location.origin}${path}`;
  }

  if (kind === 'privacy') return 'https://perdiem.app/privacy-policy';
  if (kind === 'terms') return 'https://perdiem.app/terms-of-service';
  return 'https://perdiem.app/security-policy';
}
